import React, { useState, useMemo, useEffect } from "react";
import { Link } from "react-router-dom";
import { NEET_PHYSICS_CH24_QUESTIONS } from "../data/neetPhysicsCh24Questions";

// ─────────────────────────────────────────────────────────────────────────────
// NEET Physics — Chapter 24: Ray Optics and Optical Instruments (NCERT Class 12).
// Content is drafted from the NCERT syllabus topic list; each concept has an
// explanation plus a worked example. NOT YET human-reviewed by a subject-matter
// expert — treat as a first draft and verify against NCERT before exam use.
// ─────────────────────────────────────────────────────────────────────────────

const SECTIONS = ["Reflection at Mirrors", "Refraction & Total Internal Reflection", "Lenses & Lens Maker's Formula", "Prism & Dispersion", "Optical Instruments"];

const SECTION_COLORS = {
  "Reflection at Mirrors":                    "#2563eb",
  "Refraction & Total Internal Reflection":   "#0d9488",
  "Lenses & Lens Maker's Formula":            "#7c3aed",
  "Prism & Dispersion":                       "#dc2626",
  "Optical Instruments":                      "#ca8a04",
};

const CONCEPTS = [
  // ── Reflection at Mirrors ───────────────────────────────────────────────────
  { section: "Reflection at Mirrors", title: "Laws of reflection and plane mirrors",
    explanation: "The two laws of reflection hold for any reflecting surface: (1) the angle of incidence equals the angle of reflection, both measured from the normal, and (2) the incident ray, the reflected ray, and the normal at the point of incidence all lie in the same plane. A plane mirror forms an image that is virtual, erect, laterally inverted, and the same size as the object, located as far behind the mirror as the object is in front of it.",
    example: `Law of reflection:  angle i = angle r  (from the normal)

Plane mirror image properties:
- Virtual (cannot be caught on a screen)
- Erect, same size as object
- Laterally inverted (left-right swapped)
- Image distance behind mirror = object distance in front

If the mirror itself is rotated by angle theta (incident ray
fixed), the reflected ray turns through 2*theta.` },
  { section: "Reflection at Mirrors", title: "Sign convention for spherical mirrors",
    explanation: "NEET problems use the Cartesian sign convention: all distances are measured from the pole of the mirror along the principal axis. Distances measured in the SAME direction as the incident light are positive; distances measured AGAINST the direction of incident light are negative. Heights measured upward (above the axis) are positive, downward are negative. Since the object is always on the side the light comes from, the object distance u is always negative.",
    example: `Convention (light travels left -> right onto the mirror):
Direction of incident light (rightward)      -> positive
Direction against incident light (leftward)  -> negative

Consequences:
Object distance u            -> always negative
Concave mirror: R, f          -> negative (C, F on object's side)
Convex mirror:  R, f          -> positive (C, F behind mirror)` },
  { section: "Reflection at Mirrors", title: "Mirror formula and focal length",
    explanation: "The mirror formula relates object distance u, image distance v, and focal length f: 1/v + 1/u = 1/f. For a spherical mirror of small aperture, the focal length is half the radius of curvature: f = R/2. Both f and R are negative for a concave mirror and positive for a convex mirror, following the sign convention above.",
    example: `Mirror formula:  1/v + 1/u = 1/f      and      f = R/2

Concave mirror example: object 30 cm in front, f = -10 cm
u = -30 cm, f = -10 cm
1/v = 1/f - 1/u = (1/-10) - (1/-30) = -1/10 + 1/30
    = -3/30 + 1/30 = -2/30 = -1/15
v = -15 cm   (real image, 15 cm in front of the mirror)` },
  { section: "Reflection at Mirrors", title: "Magnification and image formation — concave mirror",
    explanation: "A concave mirror's image depends on object position: beyond C -> real, inverted, diminished; at C -> real, inverted, same size; between C and F -> real, inverted, magnified; at F -> image at infinity; between F and the pole -> virtual, erect, magnified (behind the mirror, as in a shaving mirror). Linear magnification m = -v/u; negative m means inverted, |m| > 1 means magnified.",
    example: `m = -v/u

Using u = -30 cm, f = -10 cm  ->  v = -15 cm (from above)
m = -v/u = -(-15)/(-30) = -0.5

Negative -> inverted image
|m| = 0.5 < 1 -> diminished (object was beyond C)` },
  { section: "Reflection at Mirrors", title: "Magnification and image formation — convex mirror",
    explanation: "A convex mirror always forms a virtual, erect, diminished image between the pole and the focus, regardless of where the object is placed — because the reflecting surface always diverges the reflected rays. This makes convex mirrors useful for vehicle rear-view and security mirrors, giving a wider field of view than a plane mirror of the same size.",
    example: `Object 20 cm from a convex mirror, f = +10 cm
u = -20 cm, f = +10 cm
1/v = 1/f - 1/u = 1/10 - (1/-20) = 1/10 + 1/20 = 3/20
v = 20/3 ~= +6.67 cm   (virtual image behind the mirror)

m = -v/u = -(6.67)/(-20) = +0.33  -> erect, diminished` },

  // ── Refraction & Total Internal Reflection ──────────────────────────────────
  { section: "Refraction & Total Internal Reflection", title: "Refraction of light and Snell's law",
    explanation: "Refraction is the bending of light as it passes from one medium to another with a different refractive index, due to a change in speed. Snell's law states n1 sin(theta1) = n2 sin(theta2), where theta1 and theta2 are the angles of incidence and refraction (from the normal). Light bends TOWARDS the normal when entering a denser medium, and AWAY from the normal when entering a rarer medium.",
    example: `Snell's law:  n1 sin(theta1) = n2 sin(theta2)

Entering a denser medium (n2 > n1): theta2 < theta1
   -> ray bends TOWARDS the normal

Entering a rarer medium (n2 < n1): theta2 > theta1
   -> ray bends AWAY from the normal` },
  { section: "Refraction & Total Internal Reflection", title: "Refractive index",
    explanation: "The absolute refractive index of a medium is n = c/v, the ratio of the speed of light in vacuum (c) to its speed in that medium (v). Since v is always <= c, n is always >= 1. The relative refractive index of medium 2 with respect to medium 1 is n21 = n2/n1 = v1/v2, and Snell's law can be restated as n1 sin(theta1) = n2 sin(theta2).",
    example: `n = c / v          (absolute refractive index, n >= 1)

n21 = n2/n1 = v1/v2   (relative refractive index)

Example: water has n = 1.33, so light travels in water at
v = c/n = (3x10^8)/1.33 ~= 2.26 x 10^8 m/s` },
  { section: "Refraction & Total Internal Reflection", title: "Total internal reflection",
    explanation: "Total internal reflection (TIR) occurs when light travelling from a denser medium into a rarer medium strikes the boundary at an angle of incidence greater than the critical angle — the refracted ray disappears entirely and all the light is reflected back into the denser medium. TIR requires BOTH conditions: light going from denser to rarer medium, AND angle of incidence > critical angle.",
    example: `Conditions for TIR:
(1) Light travels from a DENSER to a RARER medium
(2) Angle of incidence i > critical angle theta_c

If either condition fails, some light still refracts through
(partial reflection + partial refraction, not total).` },
  { section: "Refraction & Total Internal Reflection", title: "Critical angle",
    explanation: "The critical angle theta_c is the angle of incidence (in the denser medium) for which the angle of refraction is exactly 90 degrees. Applying Snell's law at this limiting case: n sin(theta_c) = 1 x sin(90 deg) = 1, so sin(theta_c) = 1/n, where n is the refractive index of the denser medium relative to the rarer one.",
    example: `sin(theta_c) = 1/n

For glass, n = 1.5:
sin(theta_c) = 1/1.5 = 0.667
theta_c = sin^-1(0.667) ~= 41.8 degrees ~= 42 degrees` },
  { section: "Refraction & Total Internal Reflection", title: "Applications — optical fibre and mirage",
    explanation: "Optical fibres carry light signals over long distances via repeated total internal reflection: the fibre's core has a higher refractive index than its surrounding cladding, so light launched at a suitable angle keeps reflecting internally along the fibre with very little loss. A mirage forms because hot ground heats the air just above it, creating layers of progressively decreasing refractive index; light from the sky curves and undergoes TIR at a lower layer, appearing to the eye as a reflection from a distant pool of water.",
    example: `Optical fibre: n(core) > n(cladding)
  -> light entering within the acceptance angle undergoes
     repeated TIR at the core-cladding boundary, guided
     along the fibre with minimal loss

Mirage: hot road -> hot air near ground -> n decreases
  downward -> light bends and undergoes TIR near the ground` },

  // ── Lenses & Lens Maker's Formula ────────────────────────────────────────────
  { section: "Lenses & Lens Maker's Formula", title: "Refraction at a spherical surface",
    explanation: "For refraction at a single spherical surface separating two media of refractive index n1 (object side) and n2 (image side), with radius of curvature R, the relation is n2/v - n1/u = (n2 - n1)/R, using the same Cartesian sign convention (distances from the pole of the surface, along the principal axis). This single-surface formula is the building block used to derive the lens maker's formula for a thin lens (two surfaces).",
    example: `n2/v - n1/u = (n2 - n1)/R

(n1 = refractive index of the medium the light is leaving,
 n2 = refractive index of the medium the light is entering,
 R  = radius of curvature of the surface, signed)` },
  { section: "Lenses & Lens Maker's Formula", title: "Lens maker's formula",
    explanation: "The lens maker's formula gives the focal length of a thin lens of refractive index n (relative to the surrounding medium, usually air) with surface radii R1 (first surface) and R2 (second surface): 1/f = (n - 1)(1/R1 - 1/R2). For a double convex lens, R1 is positive and R2 is negative (per the sign convention), giving a positive (converging) focal length.",
    example: `1/f = (n - 1) (1/R1 - 1/R2)

Double convex lens: R1 = +20 cm, R2 = -20 cm, n = 1.5
1/f = (1.5 - 1)(1/20 - 1/(-20)) = 0.5 x (1/20 + 1/20)
    = 0.5 x (2/20) = 0.05
f = 1/0.05 = 20 cm   (converging lens)` },
  { section: "Lenses & Lens Maker's Formula", title: "Thin lens formula",
    explanation: "The thin lens formula relates object distance u, image distance v, and focal length f: 1/v - 1/u = 1/f (note the MINUS sign, unlike the mirror formula's plus sign). The same Cartesian sign convention applies: distances measured from the optical centre, in the direction of incident light are positive. A convex (converging) lens has positive f; a concave (diverging) lens has negative f.",
    example: `1/v - 1/u = 1/f

Object 30 cm from a convex lens, f = +20 cm
u = -30 cm, f = +20 cm
1/v = 1/f + 1/u = 1/20 + (1/-30) = 3/60 - 2/60 = 1/60
v = +60 cm   (real, inverted image on the far side of the lens)` },
  { section: "Lenses & Lens Maker's Formula", title: "Linear magnification of a lens",
    explanation: "Linear magnification produced by a lens is m = v/u (unlike mirrors, there is no extra minus sign in the defining ratio, because the sign convention already accounts for orientation through the signs of u and v). A negative m indicates an inverted image; |m| > 1 indicates a magnified image; |m| < 1 indicates a diminished image.",
    example: `m = v / u

Using the convex lens example: u = -30 cm, v = +60 cm
m = 60 / (-30) = -2

Negative -> inverted image
|m| = 2 -> image is twice the size of the object` },
  { section: "Lenses & Lens Maker's Formula", title: "Power of a lens",
    explanation: "The power of a lens is defined as P = 1/f, with f measured in metres, giving P in dioptres (D). A converging (convex) lens has positive power; a diverging (concave) lens has negative power. Powers of thin lenses in contact simply add, which is why opticians prescribe spectacle power directly in dioptres.",
    example: `P = 1/f    (f in metres, P in dioptres D)

Concave lens, f = -25 cm = -0.25 m
P = 1/(-0.25) = -4 D   (diverging, negative power)

Convex lens, f = +20 cm = +0.20 m
P = 1/0.20 = +5 D      (converging, positive power)` },

  // ── Prism & Dispersion ───────────────────────────────────────────────────────
  { section: "Prism & Dispersion", title: "Combination of thin lenses in contact",
    explanation: "When two thin lenses of focal lengths f1 and f2 are placed in contact, the combination behaves as a single thin lens whose power is the sum of the individual powers: P = P1 + P2, equivalently 1/F = 1/f1 + 1/f2. This lets a diverging and a converging lens partially cancel, or two converging lenses reinforce each other, useful in designing compound optical systems and corrective eyewear.",
    example: `1/F = 1/f1 + 1/f2        (equivalently P = P1 + P2)

Convex lens f1 = +20 cm, concave lens f2 = -40 cm, in contact:
1/F = 1/20 + 1/(-40) = 2/40 - 1/40 = 1/40
F = +40 cm   (net converging combination)` },
  { section: "Prism & Dispersion", title: "Refraction through a prism",
    explanation: "Light bends twice on passing through a prism — once entering, once leaving — and the total bending is called the angle of deviation, delta. For a thin prism of small refracting angle A, viewed at small angles of incidence, delta is approximately independent of the angle of incidence and is given by delta = (n - 1)A, where n is the prism material's refractive index.",
    example: `delta ~= (n - 1) A     (thin prism, small angle A)

Thin prism, A = 6 degrees, n = 1.5:
delta = (1.5 - 1)(6) = 0.5 x 6 = 3 degrees` },
  { section: "Prism & Dispersion", title: "Angle of minimum deviation",
    explanation: "As the angle of incidence on a prism is varied, the deviation delta first decreases, reaches a minimum value delta_m, then increases again. At minimum deviation, the light path through the prism is symmetric: the angle of incidence equals the angle of emergence, and the ray inside the prism travels parallel to its base. This gives the standard formula for a prism's refractive index: n = sin[(A + delta_m)/2] / sin(A/2).",
    example: `n = sin[(A + delta_m) / 2] / sin(A / 2)

At minimum deviation: angle of incidence i = angle of emergence e
   -> ray inside the prism runs parallel to the base

Used experimentally: measure A and delta_m for a prism,
then compute its refractive index n.` },
  { section: "Prism & Dispersion", title: "Dispersion of light",
    explanation: "Dispersion is the splitting of white light into its constituent colours (a spectrum) on passing through a prism, because the refractive index of the prism material depends on wavelength — it is higher for shorter wavelengths. Violet light (shortest visible wavelength) has the highest refractive index and bends the most; red light (longest wavelength) has the lowest refractive index and bends the least.",
    example: `n(violet) > n(blue) > n(green) > n(yellow) > n(orange) > n(red)

Deviation:  delta = (n - 1) A
Higher n -> larger deviation
  -> violet deviates MOST, red deviates LEAST
  -> white light spreads into VIBGYOR spectrum` },
  { section: "Prism & Dispersion", title: "Angular dispersion",
    explanation: "Angular dispersion is the difference in deviation between the two extreme colours of the visible spectrum (usually violet and red) produced by a prism: angular dispersion = delta_v - delta_r = (n_v - n_r)A, where n_v and n_r are the refractive indices for violet and red light. It measures how widely a prism spreads out the spectrum, as distinct from the mean deviation of the beam as a whole.",
    example: `Angular dispersion = delta_v - delta_r = (n_v - n_r) A

Thin prism, A = 5 degrees, n_v = 1.532, n_r = 1.514:
Angular dispersion = (1.532 - 1.514) x 5 = 0.018 x 5
                    = 0.09 degrees` },

  // ── Optical Instruments ──────────────────────────────────────────────────────
  { section: "Optical Instruments", title: "Simple microscope (magnifying glass)",
    explanation: "A simple microscope is a single converging lens used to view a small object closer than the eye's near point (D = 25 cm) would normally allow, increasing the visual angle it subtends. With the final image at infinity (relaxed eye), angular magnification m = D/f. With the final (virtual) image brought to the near point instead, m = 1 + D/f — a slightly higher magnification, at the cost of more eye strain.",
    example: `Image at infinity (relaxed eye):     m = D / f
Image at near point (strained eye):  m = 1 + D / f

Magnifying glass, f = 5 cm, D = 25 cm:
Relaxed eye:  m = 25/5 = 5
Near point:   m = 1 + 25/5 = 1 + 5 = 6` },
  { section: "Optical Instruments", title: "Compound microscope",
    explanation: "A compound microscope uses two converging lenses: a short-focal-length objective, which forms a real, inverted, highly magnified image of a nearby object, and an eyepiece, which acts as a simple magnifier on that image. The overall magnifying power (image at infinity) is approximately the product of the objective's linear magnification and the eyepiece's angular magnification: M ~= m_o x m_e = (v_o/u_o) x (D/f_e).",
    example: `M ~= m_o x m_e = (v_o / u_o) x (D / f_e)

Objective magnification m_o = 10, eyepiece f_e = 5 cm, D = 25 cm:
m_e = D / f_e = 25/5 = 5
M = m_o x m_e = 10 x 5 = 50` },
  { section: "Optical Instruments", title: "Astronomical telescope — normal adjustment",
    explanation: "An astronomical (refracting) telescope also uses two converging lenses: a large-aperture, long-focal-length objective to collect light from a distant object and form a real image, and an eyepiece to magnify that image. In normal adjustment, the final image forms at infinity, and the magnifying power is simply M = f_o/f_e — the ratio of the objective's focal length to the eyepiece's.",
    example: `Normal adjustment (final image at infinity):
M = f_o / f_e

Objective f_o = 100 cm, eyepiece f_e = 5 cm:
M = 100 / 5 = 20` },
  { section: "Optical Instruments", title: "Astronomical telescope — length and construction",
    explanation: "In normal adjustment, the objective forms its real image at its own focal point, and this coincides with the eyepiece's focal point (so the eyepiece produces a final image at infinity) — this places the two lenses a distance f_o + f_e apart, which is (approximately) the length of the telescope tube. The objective is made large in both aperture and focal length compared to the eyepiece, to gather more light from faint distant objects and to maximize magnifying power.",
    example: `Tube length (normal adjustment) L ~= f_o + f_e

Objective f_o = 100 cm, eyepiece f_e = 5 cm:
L ~= 100 + 5 = 105 cm

Objective: large aperture + large f  -> gathers more light,
           higher magnification` },
];

const S = {
  page:  { minHeight:"100vh", background:"linear-gradient(135deg,#0f172a,#1a1a2e)", fontFamily:"'Segoe UI',sans-serif", color:"#e2e8f0", padding:"18px 16px 80px",
    userSelect:"none", WebkitUserSelect:"none", MozUserSelect:"none", msUserSelect:"none" },
  back:  { color:"#94a3b8", fontSize:13, textDecoration:"none" },
  card:  { background:"rgba(255,255,255,0.06)", border:"1px solid rgba(255,255,255,0.11)", borderRadius:14, padding:"18px", marginBottom:16 },
  chip:  (active, color) => ({
    background: active ? color : "rgba(255,255,255,0.08)",
    color: active ? "#fff" : "#94a3b8",
    border:"none", borderRadius:20, padding:"6px 14px", fontSize:12.5, fontWeight:700, cursor:"pointer", marginRight:8, marginBottom:8,
  }),
  tag: (color) => ({ background:color, color:"#fff", borderRadius:20, padding:"2px 10px", fontSize:11, fontWeight:700, display:"inline-block" }),
  pre: { background:"#0b1120", border:"1px solid rgba(255,255,255,0.08)", borderRadius:10, padding:"12px 14px", fontSize:12.5, lineHeight:1.6, color:"#a5f3b8", overflowX:"auto", fontFamily:"'Fira Code','Consolas',monospace", whiteSpace:"pre" },
  option: (bg) => ({ display:"block", width:"100%", textAlign:"left", background:bg, border:"1px solid rgba(255,255,255,0.1)", borderRadius:8, padding:"9px 12px", color:"#e2e8f0", fontSize:13, marginBottom:7, cursor:"pointer" }),
  iconBtn: (bg) => ({ background:bg, border:"none", borderRadius:8, width:30, height:30, fontSize:14, lineHeight:1, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", color:"#fff", flex:"none" }),
};

// ── One interactive objective question — click an option to check it ──────────
function QuizQuestion({ item, index, onAnswer }) {
  const [selected, setSelected] = useState(null);
  const answered = selected !== null;

  const pick = (i) => {
    if (answered) return;
    setSelected(i);
    onAnswer && onAnswer(i === item.correct);
  };

  return (
    <div style={S.card}>
      <span style={S.tag(SECTION_COLORS[item.section])}>{item.section}</span>
      <span style={{ fontSize:11, color:"#64748b", marginLeft:8 }}>{item.topic}</span>
      <div style={{ fontWeight:700, fontSize:14, color:"#fff", marginTop:8, marginBottom:10 }}>{index + 1}. {item.q}</div>
      {item.options.map((opt, i) => {
        let bg = "rgba(255,255,255,0.05)";
        if (answered) {
          if (i === item.correct) bg = "rgba(34,197,94,0.28)";
          else if (i === selected) bg = "rgba(239,68,68,0.28)";
        }
        return (
          <button key={i} style={S.option(bg)} onClick={() => pick(i)} disabled={answered}>
            {String.fromCharCode(65 + i)}. {opt}
          </button>
        );
      })}
      {answered && (
        <div style={{ fontSize:12.5, color:"#94a3b8", marginTop:8, lineHeight:1.6 }}>
          {selected === item.correct ? "✅ Correct — " : "❌ Not quite — "}{item.explain}
        </div>
      )}
    </div>
  );
}

export default function NeetPhysicsCh24Page() {
  const [mode, setMode] = useState("guide"); // "guide" | "practice"
  const [activeSection, setActiveSection] = useState("All");
  const [score, setScore] = useState({ correct: 0, answered: 0 });

  // ── Bookmarks — same 🔖 pattern used elsewhere on the site, persisted locally ──
  const [bookmarks, setBookmarks] = useState(() => {
    try {
      const raw = window.localStorage.getItem("neetPhysicsCh24Bookmarks");
      return raw ? JSON.parse(raw) : [];
    } catch { return []; }
  });
  const [showBookmarkedOnly, setShowBookmarkedOnly] = useState(false);

  useEffect(() => {
    try { window.localStorage.setItem("neetPhysicsCh24Bookmarks", JSON.stringify(bookmarks)); } catch { /* ignore */ }
  }, [bookmarks]);

  const toggleBookmark = (title) => {
    setBookmarks(prev => prev.includes(title) ? prev.filter(t => t !== title) : [...prev, title]);
  };

  // ── Read aloud — same speechSynthesis pattern used on Reading Notes / TNPSC ──
  const [speakingIdx, setSpeakingIdx] = useState(null);

  useEffect(() => () => { window.speechSynthesis && window.speechSynthesis.cancel(); }, []);

  const speakConcept = (c, idx) => {
    if (!window.speechSynthesis) return;
    window.speechSynthesis.cancel();
    if (speakingIdx === idx) { setSpeakingIdx(null); return; }
    const u = new window.SpeechSynthesisUtterance(`${c.title}. ${c.explanation}`);
    u.rate = 0.95;
    u.onend = () => setSpeakingIdx(null);
    u.onerror = () => setSpeakingIdx(null);
    window.speechSynthesis.speak(u);
    setSpeakingIdx(idx);
  };

  // ── Share — native share sheet, falling back to WhatsApp like the other pages ──
  const shareConcept = (c, idx) => {
    const url = `${window.location.origin}${window.location.pathname}#concept-${idx}`;
    const text = `🧪 NEET Physics — ${c.title}\n\n${c.explanation}\n\n${url}`;
    if (navigator.share) {
      navigator.share({ title: `NEET Physics — ${c.title}`, text, url }).catch(() => {});
    } else {
      window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
    }
  };

  const filtered = useMemo(() => {
    let list = activeSection === "All" ? CONCEPTS : CONCEPTS.filter(c => c.section === activeSection);
    if (showBookmarkedOnly) list = list.filter(c => bookmarks.includes(c.title));
    return list;
  }, [activeSection, showBookmarkedOnly, bookmarks]);

  const filteredQuestions = useMemo(() => (
    activeSection === "All" ? NEET_PHYSICS_CH24_QUESTIONS : NEET_PHYSICS_CH24_QUESTIONS.filter(q => q.section === activeSection)
  ), [activeSection]);

  const handleModeChange = (m) => {
    setMode(m);
    setScore({ correct: 0, answered: 0 });
  };

  const handleAnswer = (isCorrect) => {
    setScore(s => ({ correct: s.correct + (isCorrect ? 1 : 0), answered: s.answered + 1 }));
  };

  return (
    <div
      style={S.page}
      onCopy={(e) => e.preventDefault()}
      onCut={(e) => e.preventDefault()}
      onContextMenu={(e) => e.preventDefault()}
    >
      <Link to="/neet-physics" style={S.back}>← Back to NEET Physics Menu</Link>

      <div style={{ textAlign:"center", padding:"14px 0 10px" }}>
        <div style={{ fontSize:26, fontWeight:800, color:"#fff" }}>🧪 NEET Physics — Chapter 24</div>
        <div style={{ fontSize:15, fontWeight:700, color:"#fbbf24", marginTop:4 }}>Ray Optics and Optical Instruments</div>
        <div style={{ fontSize:12.5, color:"#94a3b8", marginTop:6 }}>
          NCERT Class 12 · {CONCEPTS.length} concepts explained · {NEET_PHYSICS_CH24_QUESTIONS.length} practice questions · NOT YET human-reviewed, verify against NCERT before exam use
        </div>
      </div>

      {/* Guide / Practice mode toggle */}
      <div style={{ textAlign:"center", margin:"14px 0 8px" }}>
        <button style={S.chip(mode==="guide", "#0f766e")} onClick={() => handleModeChange("guide")}>📘 Concept Guide</button>
        <button style={S.chip(mode==="practice", "#b45309")} onClick={() => handleModeChange("practice")}>🎯 Practice Questions ({NEET_PHYSICS_CH24_QUESTIONS.length})</button>
      </div>

      {/* Section filter chips */}
      <div style={{ textAlign:"center", margin:"6px 0 18px" }}>
        <button style={S.chip(activeSection==="All", "#334155")} onClick={() => setActiveSection("All")}>All</button>
        {SECTIONS.map(s => (
          <button key={s} style={S.chip(activeSection===s, SECTION_COLORS[s])} onClick={() => setActiveSection(s)}>{s}</button>
        ))}
      </div>

      {mode === "practice" ? (
        <>
          {score.answered > 0 && (
            <div style={{ textAlign:"center", fontSize:13, fontWeight:700, color:"#fbbf24", marginBottom:14 }}>
              Score: {score.correct} / {score.answered} answered correctly
            </div>
          )}
          {filteredQuestions.map((item, i) => (
            <QuizQuestion key={i} item={item} index={i} onAnswer={handleAnswer} />
          ))}
        </>
      ) : (
      <>
      {/* ── Bookmark filter ── */}
      <div style={{ textAlign:"center", margin:"0 0 14px" }}>
        <button style={S.chip(showBookmarkedOnly, "#db2777")} onClick={() => setShowBookmarkedOnly(v => !v)}>
          🔖 {showBookmarkedOnly ? "Showing Bookmarked" : "Bookmarked"} ({bookmarks.length})
        </button>
      </div>

      {/* ── Quick reference table ── */}
      <div style={S.card}>
        <div style={{ fontWeight:800, fontSize:16, color:"#fff", marginBottom:12 }}>📋 Quick Reference Table</div>
        <div style={{ overflowX:"auto" }}>
          <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13 }}>
            <thead>
              <tr style={{ borderBottom:"2px solid rgba(255,255,255,0.15)" }}>
                <th style={{ textAlign:"left", padding:"6px 8px", color:"#94a3b8" }}>#</th>
                <th style={{ textAlign:"left", padding:"6px 8px", color:"#94a3b8" }}>Section</th>
                <th style={{ textAlign:"left", padding:"6px 8px", color:"#94a3b8" }}>Topic</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c, i) => (
                <tr key={i} style={{ borderBottom:"1px solid rgba(255,255,255,0.06)" }}>
                  <td style={{ padding:"7px 8px", color:"#64748b" }}>{i + 1}</td>
                  <td style={{ padding:"7px 8px" }}><span style={S.tag(SECTION_COLORS[c.section])}>{c.section}</span></td>
                  <td style={{ padding:"7px 8px" }}>
                    <a
                      href={`#concept-${i}`}
                      style={{ color:"#38bdf8", textDecoration:"underline", fontWeight:600 }}
                      onMouseEnter={(e) => { e.currentTarget.style.color = "#7dd3fc"; }}
                      onMouseLeave={(e) => { e.currentTarget.style.color = "#38bdf8"; }}
                    >
                      {c.title}
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Detailed concept cards ── */}
      {filtered.map((c, i) => {
        const isBookmarked = bookmarks.includes(c.title);
        const isSpeaking = speakingIdx === i;
        return (
        <div key={i} id={`concept-${i}`} style={S.card}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", gap:8, flexWrap:"wrap" }}>
            <span style={S.tag(SECTION_COLORS[c.section])}>{c.section}</span>
            <div style={{ display:"flex", gap:6 }}>
              <button
                title={isSpeaking ? "Stop reading" : "Read aloud"}
                onClick={() => speakConcept(c, i)}
                style={S.iconBtn(isSpeaking ? "#2563eb" : "rgba(37,99,235,0.22)")}
              >{isSpeaking ? "⏹" : "🔊"}</button>
              <button
                title={isBookmarked ? "Remove bookmark" : "Bookmark"}
                onClick={() => toggleBookmark(c.title)}
                style={S.iconBtn(isBookmarked ? "#db2777" : "rgba(219,39,119,0.22)")}
              >{isBookmarked ? "🔖" : "📌"}</button>
              <button
                title="Share"
                onClick={() => shareConcept(c, i)}
                style={S.iconBtn("rgba(255,255,255,0.12)")}
              >↗</button>
            </div>
          </div>
          <div style={{ fontWeight:800, fontSize:16, color:"#fff", marginTop:8 }}>{i + 1}. {c.title}</div>
          <div style={{ fontSize:13.5, color:"#cbd5e1", lineHeight:1.7, marginTop:8 }}>{c.explanation}</div>
          <div style={{ fontSize:11, fontWeight:700, color:"#94a3b8", marginTop:12, marginBottom:6, letterSpacing:0.4 }}>EXAMPLE / WORKED CALCULATION</div>
          <pre style={S.pre}><code>{c.example}</code></pre>
        </div>
        );
      })}
      </>
      )}
    </div>
  );
}
